import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import {
  AuthForm,
  AuthField,
  AuthFormError,
  AuthFormFooter,
  AuthFormHeader,
  AuthFormSuccess,
  authInputClassName,
} from '../components/auth/AuthForm';
import { AuthOrDivider } from '../components/auth/AuthOrDivider';
import { GoogleAuthButton } from '../components/auth/GoogleAuthButton';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { PasswordInput } from '../components/ui/PasswordInput';
import pt from '../locales/pt';
import { paths } from '../routes/paths';
import { useAuth } from '../store/AuthContext';

const schema = z.object({
  email: z
    .string()
    .min(1, pt.auth.errors.emailRequired)
    .email(pt.auth.errors.emailInvalid),
  password: z.string().min(1, pt.auth.errors.passwordRequired),
});

export function SignIn() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const passwordReset = Boolean(location.state?.passwordReset);

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data) => {
    const response = await login(data);
    if (!response.success) {
      let message;
      if (response.error?.non_field_errors) {
        message = pt.auth.signIn.credentialsError;
      } else if (typeof response.error === 'string') {
        message = response.error;
      } else {
        message = response.error?.detail || pt.auth.errors.generic;
      }
      setError('root.server', { message });
      return;
    }
    const target = location.state?.from?.pathname || paths.dashboard;
    navigate(target, { replace: true });
  };

  return (
    <>
      <AuthFormHeader title={pt.auth.signIn.title} subtitle={pt.auth.signIn.subtitle} />
      <AuthForm onSubmit={handleSubmit(onSubmit)}>
        {passwordReset ? (
          <AuthFormSuccess message={pt.auth.signIn.passwordResetSuccess} />
        ) : null}
        <AuthFormError message={errors.root?.server?.message} />
        <GoogleAuthButton process="login" className="w-full" size="lg" />
        <AuthOrDivider />
        <div className="flex flex-col gap-space-4 short:gap-space-1">
          <AuthField id="signin-email" label={pt.auth.fields.email} error={errors.email?.message}>
            <Input
              id="signin-email"
              type="email"
              autoComplete="email"
              invalid={!!errors.email}
              placeholder={pt.auth.fields.emailPlaceholder}
              className={authInputClassName}
              {...register('email')}
            />
          </AuthField>
          <AuthField
            id="signin-password"
            label={pt.auth.fields.password}
            error={errors.password?.message}
          >
            <PasswordInput
              id="signin-password"
              autoComplete="current-password"
              invalid={!!errors.password}
              placeholder={pt.auth.fields.passwordPlaceholder}
              className={authInputClassName}
              {...register('password')}
            />
          </AuthField>
        </div>
        <p className="text-right">
          <Link
            to="/forgot-password"
            className="text-type-body-sm font-semibold text-sage-700 hover:underline dark:text-sage-400"
          >
            {pt.auth.signIn.forgot}
          </Link>
        </p>
        <Button type="submit" size="lg" loading={isSubmitting} className="w-full">
          {isSubmitting ? pt.auth.signIn.submitting : pt.auth.signIn.submit}
        </Button>
      </AuthForm>
      <AuthFormFooter>
        <span>{pt.auth.signIn.noAccount}</span>
        <Link
          to="/sign-up"
          className="font-semibold text-sage-700 hover:underline dark:text-sage-400"
        >
          {pt.auth.signIn.goSignUp}
        </Link>
      </AuthFormFooter>
    </>
  );
}
